import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Input, TextField } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { format } from "date-fns";
import { useForm } from "react-hook-form/dist/index.ie11";
import { Controller } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import AccordionPermissionsUser from "~/components/Forms/UsersForm/AccordionPermissionsUser";
import useGetRoleUsers from "~/components/Forms/UsersForm/hooks/useGetRoleUsers";
import useGetUserAssignments from "~/components/Forms/UsersForm/hooks/useGetUserAssignments";
import useUpdateRoleUsers from "~/components/Forms/UsersForm/hooks/useUpdateRoleUsers";
import useUpdateUsers from "~/components/Forms/UsersForm/hooks/useUpdateUsers";
import Preloader from "~/components/Preloader";
import useGetDescriptionsRole from "~/pages/RegistryOfRole/hooks/useGetDescriptionsRole";
import useGetModelRole from "~/pages/RegistryOfRole/hooks/useGetModelRole";
import useGetRole from "~/pages/RegistryOfRole/hooks/useGetRole";
import { useDealersForAssignment } from "~/components/Forms/UsersForm/hooks/useDealersForAssignment";
import { useGetDirectionsV2 } from "~/entity/Directions";
import { IUserResponse } from "~/entity/Users/model/schema";
import { useGetLocationData } from "~/hooks/locationData";
import {
  Profile,
  useAddNotificationMutation,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { defaultErrorHandlerFunc } from "~/shared/hooks/useRequestHandler";
import {
  buildPermissionsMap,
  isPermissionInCollection,
} from "~/shared/lib/permissionHelpers";
import { Select } from "~/shared/ui/Select";

import { toForm, toServer } from "./model/adapter";
import { IAssignmentRow, TemporaryAssignments } from "./TemporaryAssignments";

import s from "./UsersForm.less";

interface IProps {
  subdivisionsForSelect?: any;
  dataUser?: IUserResponse;
  isLoadingUser?: boolean;
  refetchUser?: () => void;
  setCloseModal: () => void;
  setOpenDC: () => void;
  usersRefetch: () => void;
  user?: Profile;
}

const UsersForm: FC<IProps> = ({
  subdivisionsForSelect,
  setCloseModal,
  usersRefetch,
  user = {},
  dataUser,
  isLoadingUser,
  refetchUser,
  setOpenDC,
}) => {
  const temporaryAssignmentsRef = useRef(null);
  const { hasPermission } = usePermissions();
  const {
    divisionsForSelect,
    onScrollBottomDivisions,
    searchDivision,
    GetSubdivisionLoading,
  } = subdivisionsForSelect;
  const { t } = useTranslation();
  const { openChildModal } = useGetLocationData();
  const [addNotificationMutation] = useAddNotificationMutation({});
  const isFirstOpenDC = useRef(false);
  const { countDC, id: userIdFromProps } = user;
  // dataUser.user.id — основной источник (редактирование), user.id из пропса — fallback.
  // Number(undefined) = NaN уходит на бэк при создании — не допускаем.
  const id =
    dataUser?.user?.id != null ? String(dataUser.user.id) : userIdFromProps;

  const {
    data: dataUserRole,
    refetch: refetchUserRole,
    isLoading: isLoadingUserRole,
  } = useGetRoleUsers(id);

  const updateRoleUserMutation = useUpdateRoleUsers();
  const updateUserMutation = useUpdateUsers();
  const { data: dataModel } = useGetModelRole();

  const { data: dataDescription } = useGetDescriptionsRole();

  const roleSelect =
    dataModel?.roles
      ?.filter((el) => el.isActive)
      .map((el) => ({
        label: el.name,
        value: el.id,
      })) || [];

  const [savedPermissions, setSavedPermissions] = useState<
    Record<string, boolean>
  >({});

  // Дилеры приходят в REST-ответе /user/:id (dataUser.user.dealers).
  const userDealers = useMemo(
    () =>
      (dataUser?.user?.dealers ?? []).map(({ dealerName, id }) => ({
        label: dealerName,
        value: id,
      })),
    [dataUser?.user?.dealers],
  );

  // Полный список дилеров для опций селекта (GET /dealer/dealers, поиск +
  // пагинация) — userDealers покрывает только уже закреплённые.
  const {
    items: dealerItems,
    isLoading: isLoadingDealers,
    onSearch: onSearchDealers,
    onPopupScroll: onPopupScrollDealers,
  } = useDealersForAssignment();

  // Дилеры, выбранные во временных закреплениях (приходят из дочерней формы
  // TemporaryAssignments) — исключаются из permanent-селекта.
  const [tempDealerIds, setTempDealerIds] = useState<number[]>([]);
  const handleTempDealersChange = useCallback((ids: number[]) => {
    setTempDealerIds((prev) =>
      prev.length === ids.length && prev.every((v, i) => v === ids[i])
        ? prev
        : ids,
    );
  }, []);

  const schema = yup.object().shape({
    email: yup
      .string()
      .max(250, t("validation.more250"))
      .required(t("validation.required"))
      .email(t("validation.invalid_email")),
    fullname: yup.string().nullable().max(250, t("validation.more250")),
    login: yup.string().nullable().max(250, t("validation.more250")),
    position: yup.string().nullable().max(250, t("validation.more250")),
    dealerSelect: yup.array().of(yup.number()),
  });

  const { handleSubmit, control, watch, errors, setValue } = useForm({
    defaultValues: toForm(dataUser?.user, userDealers),
    resolver: yupResolver(schema),
  });

  const watchedDealerSelect = watch("dealerSelect") as number[] | undefined;

  const dealerOptions = useMemo(() => {
    // Дилеры, выбранные во временных закреплениях, исключаем из permanent-
    // селекта (кроме уже выбранных в нём) — дилеры не должны пересекаться.
    const tempSet = new Set(tempDealerIds);
    const selectedSet = new Set(watchedDealerSelect ?? []);
    const options = dealerItems
      .filter((d) => selectedSet.has(d.id) || !tempSet.has(d.id))
      .map((d) => ({
        label: d.dealerName ?? d.name ?? String(d.id),
        value: d.id,
      }));
    const present = new Set(options.map((o) => o.value));
    userDealers.forEach((d) => {
      if (
        !present.has(d.value) &&
        (selectedSet.has(d.value) || !tempSet.has(d.value))
      )
        options.push(d);
    });
    return options;
  }, [dealerItems, userDealers, tempDealerIds, watchedDealerSelect]);

  const method = useForm();

  const watchRole = watch("role");
  const [checkedPermissions, setCheckedPermissions] = useState<
    Record<string, boolean>
  >({});

  const {
    data: dataRole,
    isLoading: loadingRole,
    refetch: refetchRole,
  } = useGetRole(watchRole);

  const { data: dataAssignments, isLoading: isLoadingAssignments } =
    useGetUserAssignments(id);

  const initialAssignments = useMemo(() => {
    const temporary = dataAssignments?.data?.temporary ?? [];
    if (temporary.length === 0) return undefined;

    return temporary.map((item) => ({
      startDate: item.startDate ? new Date(item.startDate) : null,
      endDate: item.endDate ? new Date(item.endDate) : null,
      // Селект строки — permanentRmId (ОТНОСИТЕЛЬНО кого назначено).
      // temporaryRmId из ответа — это сам редактируемый пользователь,
      // в форме не используется.
      permanentRmId: item.permanentRmId ?? null,
      dealers: item.dealerIds ?? (item.dealerId != null ? [item.dealerId] : []),
      permanentRmName: item.permanentRmName ?? null,
      permanentRmLogin: item.permanentRmLogin ?? null,
      permanentRmDirections:
        item.permanentRmDirections?.map((d) => d.name) ?? [],
      serverDealers:
        item.dealers?.map((d) => ({ id: d.id, name: d.name ?? "" })) ?? [],
    })) as IAssignmentRow[];
  }, [dataAssignments]);

  useEffect(() => {
    if (userDealers.length > 0) {
      setValue(
        "dealerSelect",
        userDealers.map((d) => d.value),
      );
    }
  }, [userDealers]);

  const isDealerRole =
    // (watchRole && watchRole.value === "dealer") ||
    dataRole?.role?.dealerChoice === "one";

  const accessRoleDealer = isDealerRole && countDC > 1;

  useEffect(() => {
    if (openChildModal) {
      isFirstOpenDC.current = true;
    }
    if (!openChildModal && isFirstOpenDC.current) {
      refetchUser?.();
    }
  }, [openChildModal, refetchUser]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roleAccesses = useMemo(
    () => buildPermissionsMap(dataRole?.role?.permissions),
    [dataRole],
  );

  useEffect(() => {
    if (dataUserRole && dataUserRole?.permissions) {
      const userAccesses = buildPermissionsMap(dataUserRole?.permissions);

      setSavedPermissions({ ...roleAccesses, ...userAccesses });
      setCheckedPermissions({ ...roleAccesses, ...userAccesses });
    }
  }, [dataUserRole, roleAccesses]);

  const onSubmitRole = (data) => {
    const roleData = {
      userId: Number(id),
      permissions: Object.entries(checkedPermissions).map(
        ([accessDescriptionId, value]) => ({
          id: accessDescriptionId,
          value,
        }),
      ),
    };

    setSavedPermissions(checkedPermissions);
    setIsModalOpen(false);
  };

  const removeNewPermission = (permId: string) => {
    setCheckedPermissions((prev) => {
      const copy = { ...prev };
      delete copy[permId];
      return copy;
    });

    setSavedPermissions((prev) => {
      const copy = { ...prev };
      delete copy[permId];
      return copy;
    });
  };

  const onSubmit = (data) => {
    setIsSubmitting(true);
    const isInRolePermissions = dataRole?.role?.permissions?.map(
      (el) => el.permissionDescriptionId,
    );

    const isInCheckedPermissions =
      Object.keys(savedPermissions).length > 0 ||
      Object.keys(checkedPermissions).length > 0;

    const roleData = {
      userId: Number(id),
      permissions: isInCheckedPermissions
        ? Object.entries(checkedPermissions)
            .filter(
              ([accessDescriptionId]) =>
                !isInRolePermissions.includes(accessDescriptionId),
            )
            .map(([accessDescriptionId, value]) => ({
              id: accessDescriptionId,
              value,
            }))
        : dataUserRole?.permissions?.length > 0
          ? dataUserRole.permissions.map((el) => ({
              id: el.permissionDescriptionId,
              value: el.value,
            }))
          : [],
    };

    updateRoleUserMutation.mutate(
      { roleData },
      {
        onSuccess: () => {
          usersRefetch();
          setCheckedPermissions({});
          setSavedPermissions({});
        },
        onError: (error) => {
          console.error("Ошибка при сохранении роли:", error);
        },
      },
    );

    const roleDatas = toServer(data, Number(id));
    updateUserMutation.mutate(
      {
        roleDatas,
      },
      {
        onSuccess: async () => {
          // Сохранение temporary-закреплений. При ошибке хук бросает,
          // нотификатор уже показан внутри хука (defaultErrorHandlerFunc).
          // Молча выходим — модалку НЕ закрываем, пользователь увидит нотиф
          // и сможет исправить данные и повторить.
          try {
            await temporaryAssignmentsRef.current?.submit();
          } catch (e) {
            console.error(
              "[UsersForm] temporary assignments submit failed:",
              e,
            );
            setIsSubmitting(false);
            return;
          }
          setIsSubmitting(false);
          setCloseModal();
        },
        onError: (error) => {
          setIsSubmitting(false);
          // Ошибка сохранения пользователя — нотиф через хук мутации,
          // модалку НЕ закрываем.
          defaultErrorHandlerFunc({
            e: error,
            t,
            addNotificationMutation,
          });
        },
      },
    );
  };

  // Признаки региональной роли — read-only индикаторы из данных роли
  // (чекбоксы «Региональный менеджер» / «Руководитель региональных
  // менеджеров» удалены как рудименты, PASDILER-2066).
  const isRegionalRole = Boolean(
    dataRole?.role?.isRegional || dataRole?.role?.isLeaderRegional,
  );

  // Направления региональной роли (объединение РМ и Руководителя РМ) —
  // для read-only поля «Направление» в основной форме.
  const roleDirections = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...(dataRole?.role?.regionalPositionDirections ?? []),
            ...(dataRole?.role?.leaderPositionDirections ?? []),
          ].map((d: { instanceId: number }) => d.instanceId),
        ),
      ),
    [dataRole],
  );

  const { data: directionsData } = useGetDirectionsV2({
    status: "active",
    ActualOn: format(new Date(), "yyyy-MM-dd"),
  });
  const directionOptions = (directionsData?.items || []).map((item) => ({
    label: item.name,
    value: item.instanceId,
  }));

  const dealerChoice = dataRole?.role?.dealerChoice;

  const getNewPermissionsToDisplay = () => {
    const result: Array<{
      id: string;
      description: string;
      moduleDescription: string;
    }> = [];
    if (!dataDescription?.modules) return result;

    dataDescription.modules.forEach((mod) => {
      (mod.permissions ?? []).forEach((access) => {
        if (isPermissionInCollection(dataRole?.role?.permissions, access.id)) {
          return;
        }
        const checked =
          savedPermissions[access.id] ?? checkedPermissions[access.id];
        if (checked === true) {
          result.push({
            id: access.id,
            description: access.description,
            moduleDescription: mod.description,
          });
        }
      });
    });
    return result;
  };

  const isSubmitDisabled =
    isSubmitting ||
    (isDealerRole
      ? (watch("dealerSelect")?.length ?? 0) > 1
      : accessRoleDealer || !!isModalOpen);

  if (isLoadingUserRole || isLoadingUser) {
    return <Preloader />;
  }

  return (
    <form className={s.ind_form} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={16}>
        <Controller
          name="fullname"
          control={control}
          render={({ onChange, value }) => (
            <div className={s.field}>
              <span className={s.fieldLabel}>{t("users.fullname")}</span>
              <Input
                value={value ?? ""}
                onChange={onChange}
                status={errors["fullname"] ? "error" : "default"}
                errorText={errors["fullname"]?.message}
                style={{ width: "100%" }}
              />
            </div>
          )}
        />
        <Flex gap={16}>
          <Controller
            name="login"
            control={control}
            render={({ onChange, value }) => (
              <div className={s.field} style={{ flex: 1 }}>
                <span className={s.fieldLabel}>{t("users.login")}</span>
                <Input
                  value={value ?? ""}
                  onChange={onChange}
                  status={errors["login"] ? "error" : "default"}
                  errorText={errors["login"]?.message}
                  style={{ width: "100%" }}
                />
              </div>
            )}
          />
          <Controller
            name="position"
            control={control}
            render={({ onChange, value }) => (
              <div className={s.field} style={{ flex: 1 }}>
                <span className={s.fieldLabel}>{t("users.position")}</span>
                <Input
                  value={value ?? ""}
                  onChange={onChange}
                  status={errors["position"] ? "error" : "default"}
                  errorText={errors["position"]?.message}
                  style={{ width: "100%" }}
                />
              </div>
            )}
          />
        </Flex>
        <Flex gap={16}>
          <Controller
            name="email"
            control={control}
            render={({ onChange, value }) => (
              <div className={s.field} style={{ flex: 1 }}>
                <span className={s.fieldLabel}>{t("users.email")}</span>
                <Input
                  value={value ?? ""}
                  onChange={onChange}
                  type="email"
                  status={errors["email"] ? "error" : "default"}
                  errorText={errors["email"]?.message}
                  style={{ width: "100%" }}
                />
              </div>
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ onChange, value }) => (
              <div className={s.field} style={{ flex: 1 }}>
                <span className={s.fieldLabel}>{t("users.phone")}</span>
                <Input
                  value={value ?? ""}
                  onChange={onChange}
                  style={{ width: "100%" }}
                />
              </div>
            )}
          />
        </Flex>
        <Controller
          name="role"
          control={control}
          render={({ onChange, value }) => (
            <div className={s.field}>
              <span className={s.fieldLabel}>{t("users.role")}</span>
              <Select
                value={value}
                onChange={onChange}
                options={roleSelect}
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
              />
            </div>
          )}
        />
        <Controller
          name="subdivisionSelect"
          control={control}
          render={({ onChange, value }) => (
            <div className={s.field}>
              <span className={s.fieldLabel}>{t("users.subdivision")}</span>
              <Select
                value={value}
                onChange={onChange}
                labelInValue
                options={divisionsForSelect}
                showSearch
                filterOption={false}
                allowClear
                placeholder={t("users.emptyDivision")}
                onSearch={searchDivision}
                onPopupScroll={(e) => {
                  const t = e.currentTarget;
                  if (t.scrollTop + t.clientHeight >= t.scrollHeight - 10) {
                    onScrollBottomDivisions();
                  }
                }}
                loading={GetSubdivisionLoading}
                style={{ width: "100%" }}
              />
            </div>
          )}
        />
        <Flex gap={16} align="flex-start">
          <div className={s.field} style={{ flex: 1 }}>
            <span className={s.fieldLabel}>{t("roles.direction")}</span>
            {/* Read-only индикатор направлений региональной роли (вместо
                удалённых чекбоксов РМ / Руководитель РМ). */}
            <Select
              disabled
              mode="multiple"
              value={roleDirections}
              options={directionOptions}
              maxTagCount="responsive"
              style={{ width: "100%" }}
            />
          </div>
          <div className={s.field} style={{ flex: 1 }}>
            <span className={s.fieldLabel}>{t("users.allDC")}</span>
            {/* Пока роль грузится, dealerChoice undefined — не показываем
                маркер «Нет дилеров», чтобы не было дёргания в селект. */}
            {!loadingRole && dealerChoice === "all" && (
              <TextField value={t("users.all_dealers")} ellipsis />
            )}
            {!loadingRole &&
              (dealerChoice === "one" || dealerChoice === "multiple") && (
                <Controller
                  name="dealerSelect"
                  control={control}
                  render={({ onChange, value }) => (
                    <Select
                      mode="multiple"
                      value={value ?? []}
                      onChange={(val) => {
                        onChange(val);
                        // antd чистит строку поиска при выборе, но onSearch("")
                        // не вызывает — сбрасываем поиск в хуке вручную, иначе
                        // список остаётся отфильтрованным без перезапроса.
                        onSearchDealers("");
                      }}
                      options={dealerOptions}
                      maxTagCount="responsive"
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={onSearchDealers}
                      onPopupScroll={onPopupScrollDealers}
                      loading={isLoadingUser || isLoadingDealers}
                      style={{ width: "100%" }}
                    />
                  )}
                />
              )}
            {!loadingRole &&
              dealerChoice !== "all" &&
              dealerChoice !== "one" &&
              dealerChoice !== "multiple" && (
                <TextField value={t("users.no_dealers")} ellipsis />
              )}
          </div>
        </Flex>
        {(isDealerRole
          ? (watch("dealerSelect")?.length ?? 0) > 1
          : accessRoleDealer) && (
          <span className={s.accessRoleDealer}>{t("errors.oneDealer")}</span>
        )}
        {!isDealerRole && isModalOpen && (
          <span className={s.accessRoleDealer}>
            {t("errors.savePermissionsFirst")}
          </span>
        )}
        <div className={s.modalOverlay}>
          <div className={s.modalContent}>
            {getNewPermissionsToDisplay().map((access) => (
              <div key={access.id} className={s.textRole}>
                {access.description} <span>({access.moduleDescription})</span>
                {hasPermission("users.access.roles.user.edit") && (
                  <span
                    className={s.deletePermission}
                    onClick={() => removeNewPermission(access.id)}
                  >
                    ✕
                  </span>
                )}
              </div>
            ))}

            {dataRole && isModalOpen && (
              <div>
                <div className={s.modalTitle}> Добавить доступы:</div>
                <AccordionPermissionsUser
                  roleActive={dataRole?.role || null}
                  title={"Модули и функции"}
                  userForm
                  modules={dataDescription?.modules || []}
                  savedPermissions={savedPermissions}
                  userModules={dataUserRole?.permissions || []}
                  onPermissionsChange={setCheckedPermissions}
                  onSavedPermissions={setSavedPermissions}
                />
                <Button
                  size="medium"
                  fontSize="14"
                  bold
                  radius={5}
                  style={{ marginTop: 10 }}
                  onClick={method.handleSubmit(onSubmitRole)}
                >
                  Сохранить изменения
                </Button>
              </div>
            )}
            {!isModalOpen && hasPermission("users.access.roles.user.edit") && (
              <Button
                color="secondary"
                size="medium"
                fontSize="14"
                bold
                radius={5}
                style={{ marginTop: 10 }}
                onClick={() => setIsModalOpen(true)}
              >
                Добавить доступы
              </Button>
            )}
          </div>
        </div>
        {isRegionalRole &&
          (isLoadingAssignments ? (
            <div style={{ marginTop: 16 }}>
              <Preloader />
            </div>
          ) : (
            <TemporaryAssignments
              ref={temporaryAssignmentsRef}
              userId={Number(id)}
              permanentDealers={userDealers}
              onDealersChange={handleTempDealersChange}
              initialAssignments={initialAssignments}
            />
          ))}
        <Flex justify="flex-end">
          <Button
            type="primary"
            htmlType="submit"
            size="medium"
            fontSize="14"
            bold
            radius={5}
            loading={isSubmitting}
            disabled={isSubmitDisabled}
          >
            {t("ui.save")}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default UsersForm;
